import { useApiClient, useErrorHandler } from "@@common";
import React, { PropsWithChildren, useEffect, useState } from "react";
import { BundleClient } from "../../api/api";
import Spinner from "../../common/components/Spinner";
import { BundleModel } from "../Models/BundleModel";
import { EmbeddedResourceModel } from "../Models/EmbeddedResourceModel";
import { EnumModel } from "../Models/EnumModel";
import { SharedComponentModel } from "../Models/SharedComponentModel";
import { ViewModel } from "../Models/ViewModel";
import BundleContext, { IBundleContext } from "./BundleContext";

export const BundleProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [value, setValue] = useState<IBundleContext | null>(null);
  const [isPending, setIsPending] = useState(true);

  const bundleClient = useApiClient(BundleClient);
  const errorHandler = useErrorHandler();

  const loadConfig = React.useCallback(async () => {
    setIsPending(true);
    try {
      // ToDo: We can make the bundle name configurable later
      const bundle = (await bundleClient.getByName("default")) as BundleModel;

      //throw new Error("Test error handling in BundleProvider"); // Simulate error for testing
      //await delay(2000); // Simulate delay for testing purposes, remove later

      const viewById = bundle.Views.reduce(
        (acc, view) => {
          acc[view.Id] = view;
          return acc;
        },
        {} as Record<string, ViewModel>
      );

      const enumById = bundle.Enums.reduce(
        (acc, enumModel) => {
          acc[enumModel.Id] = enumModel;
          return acc;
        },
        {} as Record<string, EnumModel>
      );

      const sharedComponentById = bundle.SharedComponents.reduce(
        (acc, sharedComponent) => {
          acc[sharedComponent.Id] = sharedComponent;
          return acc;
        },
        {} as Record<string, SharedComponentModel>
      );

      const embededResourceById = bundle.EmbeddedResources.reduce(
        (acc, resource) => {
          acc[resource.Id] = resource;
          return acc;
        },
        {} as Record<string, EmbeddedResourceModel>
      );

      setValue({
        Views: bundle.Views,
        ViewById: viewById,
        EnumById: enumById,
        SharedComponentById: sharedComponentById,
        Theme: bundle.Theme,
        EmbeddedResourceById: embededResourceById,
        Refresh: refresh,
      });
    } catch (error: any) {
      // If it's a 404 error, set an empty Views collection
      if (error?.status === 404 || error?.response?.status === 404) {
        setValue({
          Views: [],
          ViewById: {},
          EnumById: {},
          SharedComponentById: {},
          EmbeddedResourceById: {},
          Refresh: refresh,
        });
      } else {
        await errorHandler(error);
      }
    } finally {
      setIsPending(false);
    }
  }, [bundleClient, errorHandler]);

  const refresh = React.useCallback(async () => {
    await loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  if (!value || isPending) {
    return <Spinner />;
  }
  return <BundleContext.Provider value={value}>{children}</BundleContext.Provider>;
};

export default BundleProvider;
